import type { FC } from "react";
import { useEffect, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import styled from "styled-components";
import { colours } from "@helpers";
import useDeviceDetect from "@helpers/deviceDetect";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import { helpsiteTrackCta } from "@helpers/helpsite-mixpanel";

/* Show QR (no store badges) from 768px up so tablet/desktop see QR */
const BREAKPOINT_DESKTOP = "768px";

const APP_DOWNLOAD_BG = "#C5C5C533";

/* Mobile: dashed border, no border-radius, left-aligned */
const MobileContainer = styled.div`
	width: 100%;
	box-sizing: border-box;
	margin: 24px 0;
	padding: 10px;
	background-color: ${APP_DOWNLOAD_BG};
	border: 1px dashed #000000;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 20px;

	@media (min-width: ${BREAKPOINT_DESKTOP}) {
		display: none;
	}
`;

/* Desktop: dashed border, no border-radius, left-aligned */
const DesktopContainer = styled.div`
	width: 100%;
	box-sizing: border-box;
	margin: 24px 0;
	padding: 10px;
	background-color: ${APP_DOWNLOAD_BG};
	border: 1px dashed #000000;
	display: none;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 10px;

	@media (min-width: ${BREAKPOINT_DESKTOP}) {
		display: flex;
	}
`;

/* Icon + text row, full width so text block extends to right edge */
const ContentRow = styled.div<{ $isDesktop?: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: flex-start;
	gap: 10px;
	padding: 10px 0;
	width: 100%;
	box-sizing: border-box;
`;

/* Icon: natural size, top-aligned */
const IconWrapper = styled.div`
	flex-shrink: 0;
	display: flex;
	align-items: flex-start;
	line-height: 0;

	img {
		display: block;
		vertical-align: top;
	}
`;

/* Text: 14px, fills remaining width in row so it takes the whole width */
const Text = styled.p<{ $isDesktop?: boolean }>`
	font-family: var(--euclid-regular);
	font-size: 14px;
	font-weight: 400;
	line-height: ${(p) => (p.$isDesktop ? "1.21" : "1.5")};
	color: #000000;
	margin: 0;
	text-align: left;
	flex: 1;
	min-width: 0;
`;

const Bold = styled.span`
	font-weight: 700;
	font-size: inherit;
`;

const AppLink = styled.a`
	color: #000000;
	text-decoration: underline;
	font-weight: 700;
	font-size: inherit;

	&:hover {
		opacity: 0.9;
	}
`;

/* Mobile badges: 235x70, center-aligned */
const BadgeRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	align-items: center;
	justify-content: center;
	width: 100%;

	a {
		display: inline-block;
		line-height: 0;
	}
`;

/* Desktop: QR + caption row, width 264px, center-aligned in container */
const DesktopQRRow = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 1rem;
	width: 264px;
	align-self: center;
`;

const QRImageContainer = styled.div`
	width: 8rem;
	height: 8rem;
	flex-shrink: 0;
	mix-blend-mode: multiply;
	position: relative;

	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	@supports (mix-blend-mode: screen) {
		&:after {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-color: ${colours.MIDNIGHT_BLUE};
			mix-blend-mode: screen;
		}
	}
`;

const QRCaption = styled.p`
	font-family: var(--euclid-regular);
	font-size: 13px;
	font-weight: 400;
	line-height: 1.2;
	color: #000000;
	margin: 0;
	text-align: left;
`;

const AppDownloadInfo: FC = () => {
	const pathname = usePathname();
	const { isAndroid, isIOS, isMobile, isTablet } = useDeviceDetect();
	const [mounted, setMounted] = useState(false);

	useEffect(() => setMounted(true), []);

	const appStoreUrl =
		process.env.NEXT_PUBLIC_APP_STORE_URL || "https://flx.mobi/bQkA1fQX7db";
	const googlePlayUrl =
		process.env.NEXT_PUBLIC_GOOGLE_PLAY_URL || "https://flx.mobi/FagEfRLX7db";

	const trackApple = () => {
		mixpanelTrack("download_app_clicked", {
			cta_position: pathname,
			cta_text: "appleUrl",
		});
		helpsiteTrackCta({
			helpsite_section: "article_app_download",
			page_path: pathname || undefined,
			label: "App Store",
			cta_type: "appleUrl",
		});
	};

	const trackAndroid = () => {
		mixpanelTrack("download_app_clicked", {
			cta_position: pathname,
			cta_text: "androidUrl",
		});
		helpsiteTrackCta({
			helpsite_section: "article_app_download",
			page_path: pathname || undefined,
			label: "Google Play",
			cta_type: "androidUrl",
		});
	};

	const isMobileDevice = mounted && (isMobile || isTablet);
	const showApple = !isMobileDevice || isIOS;
	const showGoogle = !isMobileDevice || isAndroid;

	const appLinkUrl =
		isMobileDevice && isAndroid ? googlePlayUrl : appStoreUrl;
	const appLinkTrack = isMobileDevice && isAndroid ? trackAndroid : trackApple;

	const renderContentText = (isDesktop: boolean) => (
		<Text $isDesktop={isDesktop}>
			<Bold>
				Use{" "}
				<AppLink
					href={appLinkUrl}
					target="_blank"
					rel="noreferrer noopener"
					onClick={appLinkTrack}
				>
					our app
				</AppLink>{" "}
				to manage payments and billing:
			</Bold>{" "}
			Log into the felix app with the same email you used to order your SIM.
		</Text>
	);

	const badgeLinks = (
		<>
			{showApple && (
				<a
					href={appStoreUrl}
					target="_blank"
					rel="noreferrer noopener"
					onClick={trackApple}
					aria-label="Download on the App Store"
				>
					<Image
						alt="Get it on App Store"
						src="/images/AppStoreBadge.png"
						width={235}
						height={70}
					/>
				</a>
			)}
			{showGoogle && (
				<a
					href={googlePlayUrl}
					target="_blank"
					rel="noreferrer noopener"
					onClick={trackAndroid}
					aria-label="Get it on Google Play"
				>
					<Image
						alt="Get it on Google Play"
						src="/images/PlayStoreBadge.png"
						width={235}
						height={70}
					/>
				</a>
			)}
		</>
	);

	return (
		<>
			<MobileContainer>
				<ContentRow $isDesktop={false}>
					<IconWrapper>
						<img src="/icons/mobile-helpsite.svg" alt="" />
					</IconWrapper>
					{renderContentText(false)}
				</ContentRow>
				<BadgeRow>{badgeLinks}</BadgeRow>
			</MobileContainer>

			<DesktopContainer>
				<ContentRow $isDesktop>
					<IconWrapper>
						<img src="/icons/mobile-helpsite.svg" alt="" />
					</IconWrapper>
					{renderContentText(true)}
				</ContentRow>
				<DesktopQRRow>
					<QRImageContainer>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img
							src="/images/footer_qrcode.jpg"
							alt="QR code to download the Felix app"
							style={{ width: "100%" }}
						/>
					</QRImageContainer>
					<QRCaption>Scan QR code to download the app</QRCaption>
				</DesktopQRRow>
			</DesktopContainer>
		</>
	);
};

export default AppDownloadInfo;
